

import React, { useState, useEffect, useRef } from 'react';
import { Quest } from '../types';
import QuestCard from './QuestCard';
import { WindDownIcon } from './icons';

interface ActiveQuestsPanelProps {
  activeQuests: Quest[];
  onCompleteQuest: (quest: Quest) => void;
  onShowDetails: (quest: Quest) => void;
  completedQuestIds: number[];
}

const ActiveQuestsPanel: React.FC<ActiveQuestsPanelProps> = ({ activeQuests, onCompleteQuest, onShowDetails, completedQuestIds }) => {
  return (
    <div className="bg-slate-200/50 dark:bg-slate-800/50 rounded-xl p-6 border border-slate-300 dark:border-slate-700 flex flex-col min-h-0">
      <div>
        <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-1">Active Quests</h2>
        <p className="text-slate-600 dark:text-slate-400">Your chosen adventures. Complete one to earn a token!</p>
      </div>
      
      <div className="flex-1 overflow-y-auto -mr-3 pr-3 mt-6">
        {activeQuests.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {activeQuests.map(quest => (
              <QuestCard 
                key={quest.id} 
                quest={quest}
                variant="active"
                onComplete={onCompleteQuest}
                onShowDetails={onShowDetails}
                completedQuestIds={completedQuestIds}
              />
            ))}
          </div>
        ) : (
          <div className="text-center h-full flex flex-col justify-center items-center py-10 bg-white dark:bg-slate-800 rounded-lg">
            <p className="text-slate-500 dark:text-slate-400">No active quests.</p>
            <p className="text-slate-600 dark:text-slate-500 text-sm mt-1">Choose a quest from above to get started!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveQuestsPanel;


const routineItemIds = ['sound', 'write', 'snack', 'share'];

export const PostTokenRoutinePanel: React.FC = () => {
  const [checkedState, setCheckedState] = useState<Record<string, boolean>>(
    routineItemIds.reduce((acc, id) => ({ ...acc, [id]: false }), {})
  );
  const [isComplete, setIsComplete] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const handleCheckChange = (id: string) => {
    if (isComplete) return; // Prevent changes during animation
    setCheckedState(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    // Clear any pending timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    const allChecked = Object.values(checkedState).every(Boolean);
    if (allChecked) {
      setIsComplete(true);
      timeoutRef.current = window.setTimeout(() => {
        setCheckedState(routineItemIds.reduce((acc, id) => ({ ...acc, [id]: false }), {}));
        setIsComplete(false);
        timeoutRef.current = null;
      }, 2500);
    }
  }, [checkedState]);
  
  const CheckboxItem: React.FC<{id: string; children: React.ReactNode}> = ({id, children}) => (
     <li className="flex items-start">
        <div className="flex items-center h-6">
            <input
              id={id}
              name={id}
              type="checkbox"
              checked={checkedState[id]}
              onChange={() => handleCheckChange(id)}
              className="focus:ring-primary-500 h-5 w-5 text-primary-600 border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-700 rounded cursor-pointer"
              style={{'--tw-ring-color': 'var(--primary-500)' } as React.CSSProperties}
              disabled={isComplete}
            />
        </div>
        <div className="ml-3 text-sm flex-1">
            <label htmlFor={id} className={`font-medium text-slate-700 dark:text-slate-300 ${isComplete ? '' : 'cursor-pointer'}`}>
              {children}
            </label>
        </div>
     </li>
  );

  return (
    <div className={`relative bg-slate-200/50 dark:bg-slate-800/50 rounded-xl p-6 border border-slate-300 dark:border-slate-700 ${isComplete ? 'animate-success-glow' : ''}`}>
      <div className="flex items-start gap-4 mb-6">
          <div className="p-3 rounded-full bg-slate-300 dark:bg-slate-700 flex-shrink-0">
             <WindDownIcon className="w-6 h-6 text-slate-600 dark:text-slate-300" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Post-Token Wind-Down</h2>
            <p className="text-slate-600 dark:text-slate-400">Ground yourself and get ready for your next adventure.</p>
          </div>
      </div>
      
      <div className="relative">
        {isComplete && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-md z-10">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 animate-pulse">Great Job!</p>
            </div>
        )}
        <ul className="space-y-4">
            <CheckboxItem id="sound">
                <span className="font-bold">Tune into Calm:</span> Choose a relaxing sound from <a href="https://mynoise.net/" target="_blank" rel="noopener noreferrer" className="underline text-[var(--primary-600)] hover:text-[var(--primary-700)] dark:text-[var(--primary-400)] dark:hover:text-[var(--primary-500)]">mynoise.net</a> to help your brain switch gears.
            </CheckboxItem>
            <CheckboxItem id="write">
                <span className="font-bold">Capture Your Thoughts:</span> In your notebook, jot down a few sentences about your last adventure or any new ideas that popped up.
            </CheckboxItem>
            <CheckboxItem id="snack">
                <span className="font-bold">Refuel Your Body:</span> Head to the kitchen and grab a healthy snack to power up for what's next.
            </CheckboxItem>
            <CheckboxItem id="share">
                <span className="font-bold">Connect & Share:</span> Find an adult and show them something cool you created or did. Sharing your world is awesome!
            </CheckboxItem>
        </ul>
      </div>
    </div>
  );
};
